import React, { useState, useEffect } from "react";
import { useAuth } from "../../Utility/AuthProvider";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants";

const LinkedDevicesDialog = ({ isOpen, onClose }) => {
  const { jwt } = useAuth();
  const [sessions, setSessions] = useState({
    current: {},
    other: [],
  });
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDeviceDetail, setShowDeviceDetail] = useState(false);
  const [isOtherDevicesExpanded, setIsOtherDevicesExpanded] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchSessionData();
    }
  }, [isOpen]);

  const fetchSessionData = async () => {
    try {
      setIsContentLoading(true);
      setError("");

      const response = await APICall(
        "GET",
        paths.user.fetchSessions,
        undefined,
        undefined,
        jwt
      );

      console.log("Sessions response:", response);

      // Handle the nested response structure
      const sessionData = response.response || response;
      setSessions({
        current: sessionData.current || {},
        other: sessionData.other || [],
      });
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setError("Failed to load devices. Please try again.");
    } finally {
      setIsContentLoading(false);
    }
  };

  const handleLogOut = async (device) => {
    try {
      setIsContinueLoading(true);
      setError("");
      setSuccess("");

      const body = {
        session_id: device ? device.entity_id : undefined,
        logout_from_all_devices: device ? false : true,
        from_linked_devices: true,
      };

      await APICall("PUT", paths.user.logout, body, undefined, jwt);

      // Refresh the session data to remove logged out device
      await fetchSessionData();

      if (device) {
        setSuccess("Successfully logged out from the device");
      } else {
        setSuccess("Successfully logged out from all devices");
      }

      // Stay on the devices list page, don't go back to detail view
      setShowDeviceDetail(false);
      setSelectedDevice(null);
    } catch (err) {
      console.error("Error logging out:", err);
      setError("Something went wrong while logging out. Please try again.");
    } finally {
      setIsContinueLoading(false);
    }
  };

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setShowDeviceDetail(true);
    // Clear any previous messages
    setError("");
    setSuccess("");
  };

  const parseUserAgent = (deviceInfo) => {
    try {
      // Parse the device_info string which contains user agent
      const infoStr = deviceInfo.replace(/'/g, '"'); // Replace single quotes with double quotes
      const info = JSON.parse(infoStr);
      const userAgent = info.user_agent || "";

      // Detect browser from user agent
      if (userAgent.includes("Chrome")) {
        if (userAgent.includes("Edg")) return "Microsoft Edge";
        if (userAgent.includes("OPR") || userAgent.includes("Opera"))
          return "Opera";
        return "Google Chrome";
      }
      if (userAgent.includes("Firefox")) return "Mozilla Firefox";
      if (userAgent.includes("Safari") && !userAgent.includes("Chrome"))
        return "Safari";

      return "Web Browser";
    } catch (e) {
      return "Web Browser";
    }
  };

  const getDeviceIcon = (deviceMeta, deviceInfo = null) => {
    // Check if it's a web client
    if (deviceMeta?.client === "web-app") {
      return (
        <svg
          className="w-6 h-6 text-blue-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    }

    // Check device type and OS for mobile devices
    const deviceType =
      deviceMeta?.device_type?.toLowerCase() ||
      deviceMeta?.type?.toLowerCase() ||
      "";
    const deviceOS = deviceMeta?.device_os?.toLowerCase() || "";

    if (
      deviceType === "mobile" ||
      deviceType === "android" ||
      deviceOS.includes("android")
    ) {
      return (
        <svg
          className="w-6 h-6 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    } else if (deviceOS.includes("ios")) {
      return (
        <svg
          className="w-6 h-6 text-gray-800"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    } else {
      // Default to computer icon
      return (
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    }
  };

  const formatDeviceName = (deviceMeta, deviceInfo = null) => {
    if (!deviceMeta) return "Unknown Device";

    // Handle web app
    if (deviceMeta.client === "web-app") {
      if (deviceInfo) {
        const browser = parseUserAgent(deviceInfo);
        return `Web - ${browser}`;
      }
      return "Web - Browser";
    }

    // Handle mobile devices
    if (deviceMeta.device_os && deviceMeta.device_name) {
      // For Android devices, show OS and model name
      if (deviceMeta.device_os === "Android") {
        // Try to get a cleaner device name
        let deviceName = deviceMeta.device_name;
        if (deviceName === "sdk_gphone64_arm64") {
          deviceName = "Android Emulator";
        }
        return `${deviceMeta.device_os} - ${deviceName}`;
      }
      return `${deviceMeta.device_os} - ${deviceMeta.device_name}`;
    }

    // Fallback for other cases
    if (deviceMeta.device_os) {
      return `${deviceMeta.device_os} Device`;
    }

    return (
      deviceMeta.device_model || deviceMeta.device_name || "Unknown Device"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    try {
      // The API returns dates in format like "Wed, 16 Jul 2025 09:31:23 GMT"
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Unknown";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {showDeviceDetail ? "Device Details" : "Linked Devices"}
          </h2>
          <button
            onClick={() => {
              setShowDeviceDetail(false);
              setSelectedDevice(null);
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {isContentLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : showDeviceDetail && selectedDevice ? (
              /* Device Detail View */
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center space-x-3">
                    {getDeviceIcon(
                      selectedDevice.device_meta,
                      selectedDevice.device_info
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {formatDeviceName(
                          selectedDevice.device_meta,
                          selectedDevice.device_info
                        )}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {selectedDevice.location}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Device ID
                      </p>
                      <p className="text-sm text-gray-600 font-mono break-all">
                        {selectedDevice.entity_id}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Device
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDeviceName(
                          selectedDevice.device_meta,
                          selectedDevice.device_info
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Logged in
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDate(selectedDevice.created)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Location
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedDevice.location}
                      </p>
                    </div>
                    {selectedDevice.device_meta?.device_os && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          Operating System
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedDevice.device_meta.device_os}
                          {selectedDevice.device_meta.device_os_version &&
                            ` ${selectedDevice.device_meta.device_os_version}`}
                        </p>
                      </div>
                    )}
                    {selectedDevice.device_meta?.app_version && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          App Version
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedDevice.device_meta.app_version}
                          {selectedDevice.device_meta.app_build_number &&
                            ` (${selectedDevice.device_meta.app_build_number})`}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Last Active
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDate(selectedDevice.updated)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Status
                      </p>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          selectedDevice.active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {selectedDevice.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleLogOut(selectedDevice)}
                  disabled={isContinueLoading}
                  className="w-full py-3 px-4 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isContinueLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                      Logging out...
                    </div>
                  ) : (
                    "Log out from this device"
                  )}
                </button>
              </div>
            ) : (
              /* Device List View */
              <div className="space-y-6">
                <p className="text-gray-600 text-sm">
                  Here is a list of devices currently logged into your account.
                  You can sign out of each device individually or sign out from
                  all other devices at once.
                </p>

                {/* Current Device */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Current Device
                  </h3>
                  <button
                    onClick={() => handleDeviceClick(sessions.current)}
                    className="w-full text-left bg-blue-50 border border-blue-200 rounded-lg p-4 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {getDeviceIcon(
                          sessions.current?.device_meta,
                          sessions.current?.device_info
                        )}
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatDeviceName(
                              sessions.current?.device_meta,
                              sessions.current?.device_info
                            )}
                          </p>
                          <p className="text-sm text-gray-600">
                            {sessions.current?.location}
                          </p>
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </button>
                </div>

                {/* Other Devices */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                      Other Devices{" "}
                      {sessions.other.length > 0 &&
                        `(${sessions.other.length})`}
                    </h3>
                    {sessions.other.length > 0 && (
                      <button
                        onClick={() =>
                          setIsOtherDevicesExpanded(!isOtherDevicesExpanded)
                        }
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                      >
                        {isOtherDevicesExpanded ? "Collapse" : "Expand"}
                        <svg
                          className={`w-4 h-4 ml-1 transition-transform ${isOtherDevicesExpanded ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {sessions.other.length > 0 ? (
                    <div
                      className={`transition-all duration-300 ${isOtherDevicesExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
                    >
                      <div className="space-y-3 max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                        {sessions.other.map((device, index) => (
                          <button
                            key={device.entity_id || index}
                            onClick={() => handleDeviceClick(device)}
                            className="w-full text-left border-b border-gray-100 last:border-b-0 p-4 hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                {getDeviceIcon(
                                  device.device_meta,
                                  device.device_info
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {formatDeviceName(
                                      device.device_meta,
                                      device.device_info
                                    )}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {device.location}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Last active: {formatDate(device.updated)}
                                  </p>
                                </div>
                              </div>
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic py-4">
                      When you log into other devices with the same number, they
                      will appear here.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mx-6 mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex-shrink-0">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {success}
            </div>
          </div>
        )}

        {error && (
          <div className="mx-6 mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex-shrink-0">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Fixed Footer with Logout All Devices Button */}
        {!showDeviceDetail && sessions.other.length > 0 && (
          <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button
              onClick={() => handleLogOut(null)}
              disabled={isContinueLoading}
              className="w-full py-3 px-4 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isContinueLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                  Logging out from all devices...
                </div>
              ) : (
                "Log out from all devices"
              )}
            </button>
          </div>
        )}

        {/* Footer for device detail view */}
        {showDeviceDetail && (
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={() => setShowDeviceDetail(false)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to devices
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedDevicesDialog;
